/**
 * Supporting documents go from the browser STRAIGHT to storage, one file at a time:
 * the server first checks the whole selection against the quota and hands back one
 * upload target per file (`init`), the browser sends each file's bytes to storage
 * (a presigned S3 form on staging/prod; through the app in local dev, which has no
 * S3), then confirms each one (`finalize`), which verifies what landed and creates
 * the row. The bytes never pass through the app, so a 25-file drop no longer holds
 * one request open for minutes (the proxy dropped it on staging).
 *
 * Pure orchestration with injected transport — no network here (api.ts owns that).
 * Returns the same result shape as the batched uploader so the components and
 * `describeBatchFailures` are unchanged.
 */

import type { BatchedUploadResult, BatchFailure, UploadProgress } from './upload-batches';

/** One upload target from `init`. `presigned` is null in local dev → deliver
 *  through the app. `content_type` is what the S3 policy was signed for — the
 *  browser must send exactly that, not `file.type`. */
export interface DirectUploadTarget {
  filename: string;
  storage_key: string;
  content_type: string;
  presigned: { url: string; fields: Record<string, string> } | null;
}

export interface DirectUploadTransport<T> {
  init: (files: readonly File[]) => Promise<DirectUploadTarget[]>;
  toStorage: (target: DirectUploadTarget, file: File, contentType: string) => Promise<void>;
  throughApp: (target: DirectUploadTarget, file: File) => Promise<void>;
  finalize: (target: DirectUploadTarget) => Promise<T>;
}

/** Files in flight at once. Enough to keep a link busy; few enough that a slow
 *  connection still shows steady progress. */
export const DIRECT_UPLOAD_CONCURRENCY = 3;

/** Upload `files` via `transport`. `init` failing (quota, bad type) fails the whole
 *  selection with the server's reason; after that every file succeeds or fails on
 *  its own, and the result lists the failures by name and reason. */
export async function uploadDirect<T>(
  files: readonly File[],
  transport: DirectUploadTransport<T>,
  onProgress?: (p: UploadProgress) => void,
  onFileDone?: () => void,
  concurrency = DIRECT_UPLOAD_CONCURRENCY,
): Promise<BatchedUploadResult<T>> {
  const uploaded: T[] = [];
  const failed: BatchFailure[] = [];
  let done = 0;
  onProgress?.({ done, total: files.length });
  if (files.length === 0) return { uploaded, failed };

  let targets: DirectUploadTarget[];
  try {
    targets = await transport.init(files);
  } catch (err) {
    return {
      uploaded,
      failed: [{ names: files.map((f) => f.name), reason: reasonOf(err) }],
    };
  }
  // Targets come back in selection order; names may have been sanitised, so pair by index.
  const jobs = files.map((file, i) => ({ file, target: targets[i] }));

  const runOne = async ({ file, target }: (typeof jobs)[number]) => {
    try {
      if (target === undefined) throw new Error('Upload target missing.');
      if (target.presigned) {
        await transport.toStorage(target, file, target.content_type);
      } else {
        await transport.throughApp(target, file);
      }
      uploaded.push(await transport.finalize(target));
    } catch (err) {
      failed.push({ names: [file.name], reason: reasonOf(err) });
    }
    done += 1;
    onProgress?.({ done, total: files.length });
    onFileDone?.();
  };

  let next = 0;
  const worker = async () => {
    while (next < jobs.length) {
      const job = jobs[next++];
      if (job) await runOne(job);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, worker));
  return { uploaded, failed };
}

function reasonOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'Upload failed.';
}
